import { useQuery } from '@apollo/client'
import {
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Box,
  Chip,
} from '@mui/material'
import { useState } from 'react'
import styled from '@emotion/styled'
import { GET_PRODUCTS } from './queries'

interface Product {
  id: string
  name: string
  partNumber: string
  description: string
  manufacturer: string
  type: string
  quantity: number
}

interface ProductsListProps {
  selectedProduct: Product | null
  onProductSelect: (product: Product | null) => void
  excludeProductIds?: string[]
}

const normalize = (str: string) =>
  (str || '').replace(/[\s-]/g, '').toLowerCase()

const StyledListItemButton = styled(ListItemButton)<{ isActive?: boolean }>`
  &.MuiListItemButton-root {
    margin: 2px 0;
    ${({ isActive }) =>
      isActive &&
      `
      color: #0573ED;
      background-color: #f0f8ff;
      border-left: 3px solid #0573ED;
    `}
    &:hover {
      background-color: ${({ isActive }) => (isActive ? '#e6f3ff' : '#f5f5f5')};
    }
  }
`

const ProductsList = ({
  selectedProduct,
  onProductSelect,
  excludeProductIds,
}: ProductsListProps) => {
  const { data, loading, error } = useQuery(GET_PRODUCTS, {
    variables: { filters: {} },
  })
  const [search, setSearch] = useState('')

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress size={32} />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading products: {error.message}
      </Alert>
    )
  }

  const products: Product[] = data?.products || []
  const normalizedSearch = normalize(search)

  const filteredItems = products
    .filter((item) => !excludeProductIds?.includes(item.id))
    .filter((item) => {
      if (!normalizedSearch) return true
      return (
        normalize(item.partNumber).includes(normalizedSearch) ||
        normalize(item.manufacturer || '').includes(normalizedSearch) ||
        normalize(item.name || '').includes(normalizedSearch) ||
        normalize(item.description || '').includes(normalizedSearch)
      )
    })

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TextField
        label="Search products"
        size="small"
        fullWidth
        margin="dense"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by part number, manufacturer, name, or description"
        sx={{ mb: 2 }}
      />

      {filteredItems.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flex={1}
        >
          <Typography color="text.secondary" variant="body2">
            {search
              ? 'No products found matching your search.'
              : 'No products available.'}
          </Typography>
        </Box>
      ) : (
        <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
          {filteredItems.map((item) => (
            <ListItem key={item.id} disablePadding>
              <StyledListItemButton
                isActive={selectedProduct?.id === item.id}
                onClick={() =>
                  onProductSelect(selectedProduct?.id === item.id ? null : item)
                }
                sx={{ px: 2, py: 1.5 }}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        {item.name}
                      </Typography>
                      <Chip
                        label={item.type}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box mt={0.5}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        <strong>Part Number:</strong> {item.partNumber}
                      </Typography>
                      {item.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          noWrap
                        >
                          {item.description}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        <strong>Manufacturer:</strong>{' '}
                        {item.manufacturer || 'N/A'} | <strong>Qty:</strong>{' '}
                        {item.quantity}
                      </Typography>
                    </Box>
                  }
                />
              </StyledListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  )
}

export default ProductsList
